import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import clinicalNoteService from './clinicalNoteService.js';

/**
 * Convert backend enum-style values into readable labels.
 *
 * Example:
 * READY_FOR_REVIEW -> Ready For Review
 */
function formatEnumLabel(value) {
  if (!value) {
    return '';
  }

  return String(value)
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map(
      (part) =>
        part.charAt(0).toUpperCase() +
        part.slice(1)
    )
    .join(' ');
}

/**
 * Resolve a readable section name from the validation issue.
 */
function formatSectionName(sectionCode) {
  switch (sectionCode) {
    case 'DIAGNOSIS':
      return 'Diagnosis';

    case 'PAIN':
      return 'Pain';

    case 'ROM':
      return 'Range of Motion';

    case 'STRENGTH':
      return 'Strength';

    case 'GOAL':
      return 'Goals';

    case 'SPECIAL_TEST':
      return 'Special Tests';

    case 'PALPATION':
      return 'Palpation';

    case 'CONTENT':
      return 'Clinical Content';

    case 'TREATMENT':
      return 'Treatment';

    case 'PLAN_OF_CARE':
      return 'Plan of Care';

    case 'SIGNATURE':
      return 'Signature';

    case 'NOTE':
      return 'Clinical Note';

    default:
      return formatEnumLabel(
        sectionCode
      ) || 'Other';
  }
}

/**
 * Determine one issue's frontend visual classification.
 */
function getIssueType(issue) {
  if (issue?.warning) {
    return issue?.acknowledged
      ? 'acknowledged-warning'
      : 'warning';
  }

  if (issue?.blocking) {
    return 'blocking';
  }

  return 'information';
}

/**
 * Display one validation issue.
 */
function ValidationIssueCard({
  issue,
}) {
  const issueType =
    getIssueType(issue);

  let border =
    '1px solid #d7d7d7';

  let background =
    '#fafafa';

  let statusText =
    'Information';

  let statusColor =
    '#555';

  if (
    issueType ===
    'blocking'
  ) {
    border =
      '1px solid #b42318';

    background =
      '#fff5f5';

    statusText =
      'Blocking';

    statusColor =
      '#b42318';
  }

  if (
    issueType ===
    'warning'
  ) {
    border =
      '1px solid #d6a100';

    background =
      '#fff8df';

    statusText =
      'Warning';

    statusColor =
      '#8a6500';
  }

  if (
    issueType ===
    'acknowledged-warning'
  ) {
    border =
      '1px solid #a8d5b0';

    background =
      '#f4fbf5';

    statusText =
      'Warning Acknowledged';

    statusColor =
      '#276738';
  }

  return (
    <div
      style={{
        border,
        background,
        borderRadius: 8,
        padding: 11,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems:
            'flex-start',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            style={{
              display: 'flex',
              gap: 7,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <strong
              style={{
                fontSize: 13,
              }}
            >
              {issue.fieldDisplayName ||
                formatSectionName(
                  issue.sectionCode
                )}
            </strong>

            {issue.issueCode && (
              <span
                style={{
                  borderRadius: 999,
                  background:
                    '#eef1f4',
                  padding:
                    '2px 7px',
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                {formatEnumLabel(
                  issue.issueCode
                )}
              </span>
            )}
          </div>

          {issue.fieldCode && (
            <div
              style={{
                marginTop: 3,
                fontSize: 11,
                color: '#777',
              }}
            >
              {issue.fieldCode}
            </div>
          )}
        </div>

        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color:
              statusColor,
          }}
        >
          {statusText}
        </div>
      </div>

      <div
        style={{
          marginTop: 7,
          fontSize: 13,
          lineHeight: 1.45,
        }}
      >
        {issue.message ||
          'Validation issue detected.'}
      </div>

      {issue.recordId && (
        <div
          style={{
            marginTop: 6,
            fontSize: 10,
            color: '#888',
          }}
        >
          Record #{issue.recordId}
        </div>
      )}
    </div>
  );
}

/**
 * Display one section group.
 */
function ValidationSectionGroup({
  sectionCode,
  issues,
}) {
  const blockingCount =
    issues.filter(
      (issue) =>
        issue.blocking
    ).length;

  const unresolvedWarningCount =
    issues.filter(
      (issue) =>
        issue.warning &&
        !issue.acknowledged
    ).length;

  return (
    <section
      style={{
        border:
          '1px solid #ddd',
        borderRadius: 9,
        overflow: 'hidden',
        background: '#fff',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          gap: 10,
          alignItems: 'center',
          flexWrap: 'wrap',
          padding:
            '9px 11px',
          background:
            '#f7f8fa',
          borderBottom:
            '1px solid #ddd',
        }}
      >
        <strong>
          {formatSectionName(
            sectionCode
          )}
        </strong>

        <div
          style={{
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
            fontSize: 11,
          }}
        >
          {blockingCount > 0 && (
            <span
              style={{
                borderRadius: 999,
                padding:
                  '2px 7px',
                background:
                  '#fce8e6',
                color: '#8a1c13',
                fontWeight: 700,
              }}
            >
              {blockingCount}{' '}
              blocking
            </span>
          )}

          {unresolvedWarningCount >
            0 && (
            <span
              style={{
                borderRadius: 999,
                padding:
                  '2px 7px',
                background:
                  '#fff3cd',
                color: '#795500',
                fontWeight: 700,
              }}
            >
              {
                unresolvedWarningCount
              }{' '}
              warning
              {unresolvedWarningCount ===
              1
                ? ''
                : 's'}
            </span>
          )}
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gap: 9,
          padding: 10,
        }}
      >
        {issues.map(
          (issue, index) => (
            <ValidationIssueCard
              key={[
                issue.sectionCode ||
                  'section',

                issue.fieldCode ||
                  'field',

                issue.issueCode ||
                  'issue',

                issue.recordId ||
                  'record',

                index,
              ].join('|')}
              issue={issue}
            />
          )
        )}
      </div>
    </section>
  );
}

/**
 * Complete clinical-note validation panel.
 *
 * Backend response fields used:
 *
 * - status
 * - entityVersion
 * - valid
 * - canMoveToReview
 * - canSign
 * - hasBlockingIssues
 * - hasUnresolvedWarnings
 * - diagnosisReady
 * - blockingIssueCount
 * - warningIssueCount
 * - issues[]
 *
 * Validation remains authoritative on Spring Boot.
 */
export default function ClinicalNoteValidationPanel({
  clinicalNoteId,
  enabled = true,
  refreshToken = 0,
  onValidation,
  onVersionChange,
}) {
  const [
    validation,
    setValidation,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState('');

  /**
   * Run complete backend validation.
   */
  const loadValidation =
    useCallback(
      async () => {
        if (
          !enabled ||
          !clinicalNoteId
        ) {
          return;
        }

        setLoading(true);
        setError('');

        try {
          const response =
            await clinicalNoteService
              .validateClinicalNote(
                clinicalNoteId
              );

          setValidation(
            response
          );

          if (
            response?.entityVersion !==
              null &&
            response?.entityVersion !==
              undefined &&
            typeof onVersionChange ===
              'function'
          ) {
            onVersionChange(
              response.entityVersion,
              response
            );
          }

          if (
            typeof onValidation ===
            'function'
          ) {
            onValidation(
              response
            );
          }
        } catch (loadError) {
          setError(
            loadError?.message ||
              'Clinical note validation could not be completed.'
          );
        } finally {
          setLoading(false);
        }
      },
      [
        clinicalNoteId,
        enabled,
        onValidation,
        onVersionChange,
      ]
    );

  /**
   * Refresh when the caller changes refreshToken.
   *
   * This will later let ClinicalNotePage refresh validation after:
   *
   * - autosave completion
   * - warning acknowledgement
   * - Plan of Care completion
   * - workflow transitions
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    loadValidation();
  }, [
    loadValidation,
    refreshToken,
    enabled,
  ]);

  const groupedIssues =
    useMemo(() => {
      const groups =
        new Map();

      const issues =
        Array.isArray(
          validation?.issues
        )
          ? validation.issues
          : [];

      issues.forEach(
        (issue) => {
          const sectionCode =
            issue?.sectionCode ||
            'OTHER';

          if (
            !groups.has(
              sectionCode
            )
          ) {
            groups.set(
              sectionCode,
              []
            );
          }

          groups
            .get(
              sectionCode
            )
            .push(
              issue
            );
        }
      );

      return Array.from(
        groups.entries()
      );
    }, [validation]);

  if (!enabled) {
    return null;
  }

  return (
    <section
      style={{
        border:
          '1px solid #d7d7d7',
        borderRadius: 10,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* =====================================================
          HEADER
          ===================================================== */}
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          padding:
            '13px 16px',
          background:
            '#f7f8fa',
          borderBottom:
            '1px solid #ddd',
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: 17,
            }}
          >
            Clinical Note Validation
          </h3>

          <div
            style={{
              marginTop: 3,
              fontSize: 12,
              color: '#666',
            }}
          >
            Backend validation determines
            whether this note may move
            to review or signing.
          </div>
        </div>

        <button
          type="button"
          disabled={loading}
          onClick={
            loadValidation
          }
        >
          {loading
            ? 'Validating…'
            : 'Refresh Validation'}
        </button>
      </div>

      <div
        style={{
          padding: 16,
        }}
      >
        {error && (
          <div
            role="alert"
            style={{
              marginBottom: 14,
              border:
                '1px solid #b42318',
              borderRadius: 8,
              background:
                '#fff5f5',
              padding: 11,
            }}
          >
            <strong>
              Validation failed.
            </strong>

            <div
              style={{
                marginTop: 4,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          </div>
        )}

        {!validation &&
          !error &&
          loading && (
          <div>
            Running clinical-note
            validation…
          </div>
        )}

        {validation && (
          <>
            {/* ===============================================
                STATUS SUMMARY
                =============================================== */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns:
                  'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 10,
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  border:
                    '1px solid #ddd',
                  borderRadius: 8,
                  padding: 10,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    color: '#777',
                    fontWeight: 700,
                    textTransform:
                      'uppercase',
                  }}
                >
                  Status
                </div>

                <div
                  style={{
                    marginTop: 4,
                    fontWeight: 700,
                  }}
                >
                  {formatEnumLabel(
                    validation.status
                  ) || 'Unknown'}
                </div>
              </div>

              <div
                style={{
                  border:
                    validation.valid
                      ? '1px solid #a8d5b0'
                      : '1px solid #d6a100',
                  borderRadius: 8,
                  padding: 10,
                  background:
                    validation.valid
                      ? '#f4fbf5'
                      : '#fff8df',
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    color: '#777',
                    fontWeight: 700,
                    textTransform:
                      'uppercase',
                  }}
                >
                  Overall Validation
                </div>

                <div
                  style={{
                    marginTop: 4,
                    fontWeight: 700,
                  }}
                >
                  {validation.valid
                    ? 'Valid'
                    : 'Needs Attention'}
                </div>
              </div>

              <div
                style={{
                  border:
                    validation
                      .canMoveToReview
                      ? '1px solid #a8d5b0'
                      : '1px solid #ddd',
                  borderRadius: 8,
                  padding: 10,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    color: '#777',
                    fontWeight: 700,
                    textTransform:
                      'uppercase',
                  }}
                >
                  Ready For Review
                </div>

                <div
                  style={{
                    marginTop: 4,
                    fontWeight: 700,
                  }}
                >
                  {validation
                    .canMoveToReview
                    ? 'Yes'
                    : 'No'}
                </div>
              </div>

              <div
                style={{
                  border:
                    validation.canSign
                      ? '1px solid #a8d5b0'
                      : '1px solid #ddd',
                  borderRadius: 8,
                  padding: 10,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    color: '#777',
                    fontWeight: 700,
                    textTransform:
                      'uppercase',
                  }}
                >
                  Can Sign
                </div>

                <div
                  style={{
                    marginTop: 4,
                    fontWeight: 700,
                  }}
                >
                  {validation.canSign
                    ? 'Yes'
                    : 'No'}
                </div>
              </div>
            </div>

            {/* ===============================================
                COUNTS
                =============================================== */}
            <div
              style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
                marginBottom: 14,
              }}
            >
              <span
                style={{
                  borderRadius: 999,
                  padding:
                    '4px 9px',
                  background:
                    validation
                      .blockingIssueCount >
                    0
                      ? '#fce8e6'
                      : '#eef3f8',
                  color:
                    validation
                      .blockingIssueCount >
                    0
                      ? '#8a1c13'
                      : '#334e68',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {
                  validation
                    .blockingIssueCount
                }{' '}
                blocking{' '}
                {validation
                  .blockingIssueCount ===
                1
                  ? 'issue'
                  : 'issues'}
              </span>

              <span
                style={{
                  borderRadius: 999,
                  padding:
                    '4px 9px',
                  background:
                    validation
                      .warningIssueCount >
                    0
                      ? '#fff3cd'
                      : '#eef3f8',
                  color:
                    validation
                      .warningIssueCount >
                    0
                      ? '#795500'
                      : '#334e68',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {
                  validation
                    .warningIssueCount
                }{' '}
                {validation
                  .warningIssueCount ===
                1
                  ? 'warning'
                  : 'warnings'}
              </span>

              <span
                style={{
                  borderRadius: 999,
                  padding:
                    '4px 9px',
                  background:
                    validation
                      .diagnosisReady
                      ? '#e7f6ea'
                      : '#fce8e6',
                  color:
                    validation
                      .diagnosisReady
                      ? '#276738'
                      : '#8a1c13',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                Diagnosis{' '}
                {validation
                  .diagnosisReady
                  ? 'Ready'
                  : 'Not Ready'}
              </span>

              {validation
                .hasUnresolvedWarnings && (
                <span
                  style={{
                    borderRadius: 999,
                    padding:
                      '4px 9px',
                    background:
                      '#fff3cd',
                    color:
                      '#795500',
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Unresolved Warnings
                </span>
              )}
            </div>

            {/* ===============================================
                ALL CLEAR
                =============================================== */}
            {groupedIssues.length ===
              0 && (
              <div
                style={{
                  border:
                    '1px solid #a8d5b0',
                  borderRadius: 8,
                  background:
                    '#f4fbf5',
                  padding: 12,
                  color: '#276738',
                  fontWeight: 700,
                }}
              >
                No clinical validation
                issues were found.
              </div>
            )}

            {/* ===============================================
                GROUPED ISSUES
                =============================================== */}
            {groupedIssues.length >
              0 && (
              <div
                style={{
                  display: 'grid',
                  gap: 12,
                }}
              >
                {groupedIssues.map(
                  ([
                    sectionCode,
                    issues,
                  ]) => (
                    <ValidationSectionGroup
                      key={
                        sectionCode
                      }
                      sectionCode={
                        sectionCode
                      }
                      issues={
                        issues
                      }
                    />
                  )
                )}
              </div>
            )}

            {validation.entityVersion !==
              null &&
              validation.entityVersion !==
                undefined && (
              <div
                style={{
                  marginTop: 12,
                  textAlign: 'right',
                  fontSize: 10,
                  color: '#999',
                }}
              >
                Validated note version{' '}
                {
                  validation.entityVersion
                }
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}